import BatchPredictionIcon from '@mui/icons-material/BatchPrediction';
import {
  Box,
  Checkbox,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
} from '@mui/material';
import { EditIcon, FileCheck2, Trash } from 'lucide-react';
import * as React from 'react';
import { Link } from 'react-router-dom';
import { v4 as randomID } from 'uuid';
import LoadingFullScreen from './LoadingFullScreen';
export type Column<T = any> = {
  id: keyof T | string;
  label: string;
  minWidth?: number;
  width?: number | string;
  maxWidth?: number | string;
  align?: 'right' | 'left' | 'center';
  link?: boolean;
  className?: string;
  render?: (row: T) => React.ReactNode;
  format?: (value: any) => string | React.ReactNode;
  getLink?: (row: T) => string;
  onClick?: (row: T) => void;
  allowTextWrap?: boolean;
  showTooltip?: boolean;
};

export type CustomActionType<T extends object> = {
  title?: string;
  Element: React.FC<{ row: T }>;
  disabled?: boolean;
};

type TableDataProps<T extends object> = {
  columns: Column<T>[];
  data: T[];
  total: number;
  page: number;
  rowsPerPage?: number;
  rowsPerPageOptions?: number[];
  isLoading?: boolean;
  showActionCell?: boolean;
  showCheckbox?: boolean;
  labelCheckBox?: string;
  disabledEdit?: (rowData: T) => boolean;
  disabledEditRate?: (rowData: T) => boolean;
  disabledComplete?: (rowData: T) => boolean;
  disabledSelectRow?: boolean | ((rowData: T) => boolean);
  idField?: keyof T; // Field name để lấy ID của từng row
  selectedRows?: Set<any>; // Sử dụng any để có thể là string hoặc number
  onSelectionChange?: (row: T) => void;
  onSelectionAllChange?: (data: T[], isChecked: boolean) => void;
  showDetailAction?: (rowData: T) => boolean;
  showCompleteAction?: (rowData: T) => boolean;
  customAction?: CustomActionType<T>[];
  onDelete?: (rowData: T) => void;
  onDetail?: (rowData: T) => void;
  onEditRate?: (rowData: T) => void;
  onEdit?: (rowData: T) => void;
  onPageChange: (page: number, rowsPerPage: number) => void;
};

export function TableData<T extends object>({
  columns,
  data = [],
  total,
  page = 0,
  rowsPerPage = 10,
  rowsPerPageOptions = [10, 25, 100],
  onPageChange,
  isLoading = false,
  showActionCell = false,
  showCheckbox = false,
  labelCheckBox = 'Chọn tất cả',
  disabledEdit = (rowData) => false,
  disabledEditRate = (rowData) => false,
  disabledComplete = (rowData) => false,
  disabledSelectRow = false,
  idField = 'id' as keyof T,
  selectedRows = new Set(),
  onSelectionChange,
  onSelectionAllChange,
  showDetailAction = () => false,
  showCompleteAction = () => false,
  onDetail,
  onEdit,
  onEditRate,
  onDelete,
  ...resp
}: TableDataProps<T>) {
  React.useEffect(() => {
    if (!isLoading && data.length === 0 && page > 0) {
      const maxPage = Math.ceil(total / rowsPerPage) - 1;
      if (page > maxPage) {
        onPageChange(page - 1, rowsPerPage);
      }
    }
  }, [data, page, rowsPerPage, total, isLoading, onPageChange]);

  // Checkbox handlers
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!onSelectionAllChange) return;

    // Truyền toàn bộ data hiện tại và trạng thái checked
    onSelectionAllChange(data, event.target.checked);
  };

  const handleSelectRow = (row: T) => {
    if (!onSelectionChange) return;

    // Truyền row data ra ngoài để xử lý
    onSelectionChange(row);
  };

  // Kiểm tra có nên disable checkbox "Select All" không
  const isSelectAllDisabled =
    typeof disabledSelectRow === 'boolean' ? disabledSelectRow : data.length > 0 ? data.every((row) => disabledSelectRow(row)) : data.length === 0;

  // Lọc ra những row không bị disabled để tính toán trạng thái checkbox
  const enabledRows = data.filter((row) => {
    const isDisabled = typeof disabledSelectRow === 'function' ? disabledSelectRow(row) : disabledSelectRow;
    return !isDisabled;
  });

  // Kiểm tra tất cả dòng enabled có được chọn không
  const isAllSelected = showCheckbox && enabledRows.length > 0 && enabledRows.every((row) => selectedRows.has(row[idField]));

  // Kiểm tra có một số dòng enabled được chọn không (indeterminate state)
  const isIndeterminate = showCheckbox && enabledRows.length > 0 && enabledRows.some((row) => selectedRows.has(row[idField])) && !isAllSelected;

  const handleChangePage = (_: unknown, newPage: number) => {
    onPageChange(newPage, rowsPerPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSize = +event.target.value;
    onPageChange(0, newSize);
  };

  return (
    <Box
      sx={{
        background: '#fff',
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        overflow: 'hidden',
        borderRadius: '16px',
        border: '1px solid #e0e0e0',
      }}
    >
      <TableContainer>
        <Table
          stickyHeader
          aria-label="generic table"
          sx={{
            minWidth: 650,
          }}
        >
          <TableHead>
            <TableRow>
              {showCheckbox && (
                <TableCell
                  align="center"
                  width={60}
                  sx={{
                    fontWeight: 'bold',
                    fontSize: 16,
                    backgroundColor: '#f5f5f5',
                  }}
                >
                  <Tooltip title={labelCheckBox}>
                    <Checkbox
                      disabled={isSelectAllDisabled}
                      indeterminate={isIndeterminate}
                      checked={isAllSelected}
                      onChange={handleSelectAll}
                      color="primary"
                    />
                  </Tooltip>
                </TableCell>
              )}
              <TableCell
                align="center"
                width={60}
                sx={{
                  fontWeight: 'bold',
                  fontSize: 16,
                  backgroundColor: '#f5f5f5',
                }}
              >
                #
              </TableCell>
              {columns.map((column) => (
                <TableCell
                  key={String(column.id) + randomID()}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                  className={column.className}
                  sx={{
                    fontWeight: 'bold',
                    fontSize: 16,
                    backgroundColor: '#f5f5f5',
                    minWidth: '100px',
                    width: column?.width,
                    maxWidth: column?.maxWidth,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {column.label}
                </TableCell>
              ))}
              {showActionCell && (
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 'bold',
                    width: '1%',
                    maxWidth: '130px',
                    fontSize: 16,
                    backgroundColor: '#f5f5f5',
                    minWidth: '100px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    position: 'sticky',
                    right: 0,
                  }}
                >
                  Hành động
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 && !isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length + (showCheckbox ? 1 : 0) + (showActionCell ? 1 : 0) + 1} align="center">
                  Không có dữ liệu
                </TableCell>
              </TableRow>
            ) : (
              data?.map((row, rowIndex) => (
                <TableRow hover role="checkbox" tabIndex={-1} key={rowIndex}>
                  {showCheckbox && (
                    <TableCell align="center">
                      <Tooltip title={labelCheckBox}>
                        <Checkbox
                          disabled={typeof disabledSelectRow === 'function' ? disabledSelectRow(row) : disabledSelectRow}
                          checked={selectedRows.has(row[idField])}
                          onChange={() => handleSelectRow(row)}
                          color="primary"
                        />
                      </Tooltip>
                    </TableCell>
                  )}
                  <TableCell align="center">{page * rowsPerPage + rowIndex + 1}</TableCell>
                  {columns.map((column) => {
                    //@ts-ignore
                    const value = row?.[column.id] ? row?.[column.id] : '';
                    const handleClick = column.onClick;

                    return (
                      <TableCell
                        key={String(column.id) + randomID()}
                        align={column.align}
                        className={column.className}
                        style={handleClick ? { cursor: 'pointer' } : {}}
                        sx={{
                          minWidth: column?.minWidth || '100px',
                          width: column?.width || '100px',
                          maxWidth: column?.maxWidth || '100px',
                          overflow: 'hidden',
                          textOverflow: column.allowTextWrap ? 'unset' : 'ellipsis',
                          whiteSpace: column.allowTextWrap ? 'pre-wrap' : 'nowrap',
                          wordBreak: column.allowTextWrap ? 'break-word' : 'normal',
                          overflowWrap: column.allowTextWrap ? 'break-word' : 'normal',
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (handleClick) handleClick(row);
                        }}
                      >
                        {column.render ? (
                          column.render(row)
                        ) : column.getLink ? (
                          <Link
                            to={column.getLink(row)}
                            onClick={(e) => e.stopPropagation()}
                            style={{
                              width: '100%',
                              display: 'block',
                              textDecoration: 'none',
                              color: '#1677ff',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {column.format ? column.format(value) : value}
                          </Link>
                        ) : column.format ? (
                          column.format(value)
                        ) : column.showTooltip ? (
                          <Tooltip title={value || '-'}>
                            <span>{value || '-'}</span>
                          </Tooltip>
                        ) : (
                          value || '-'
                        )}
                      </TableCell>
                    );
                  })}

                  {showActionCell && (
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 'bold',
                        fontSize: 16,
                        backgroundColor: '#f5f5f5',
                        minWidth: '100px',
                        width: '1%',
                        maxWidth: 'max-content',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        position: 'sticky',
                        right: 0,
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" divider={<Divider orientation="vertical" flexItem />}>
                        {showDetailAction(row) && (
                          <Tooltip title="Xem danh sách dự đoán">
                            <span>
                              <IconButton disabled={disabledComplete(row)} color="primary" onClick={() => onDetail && onDetail(row)}>
                                <BatchPredictionIcon fontSize="medium" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {showCompleteAction(row) && (
                          <Tooltip title="Cập nhật tỉ số trận đấu">
                            <span>
                              <IconButton disabled={disabledEditRate(row)} color="primary" onClick={() => onEditRate && onEditRate(row)}>
                                <FileCheck2 fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {resp.customAction?.map((action, index) => {
                          const { Element, title } = action;
                          return (
                            <Tooltip key={index} title={title || ''}>
                              <Element row={row} />
                            </Tooltip>
                          );
                        })}
                        {onEdit && (
                          <Tooltip title="Chỉnh sửa">
                            <span>
                              <IconButton disabled={disabledEdit(row)} color="primary" onClick={() => onEdit(row)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        {onDelete && (
                          <Tooltip title="Xóa">
                            <IconButton color="error" onClick={() => onDelete(row)}>
                              <Trash fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <Box>
        <TablePagination
          sx={{ borderTop: '1px solid #e0e0e0', background: '#f5f5f5' }}
          rowsPerPageOptions={rowsPerPageOptions}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Số bản ghi/trang"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} trong tổng ${count !== -1 ? count : `hơn ${to}`}`}
        />
      </Box>

      {isLoading && <LoadingFullScreen open={isLoading} />}
    </Box>
  );
}
